import ifConventionAxios from "../../../api/ifConventionAxios";

// BASE_URL not needed - ifConventionAxios handles base URL configuration
const BASE_URL = ""; // Empty since ifConventionAxios has baseURL configured

// -----------------------------------------------------------------------------
// Config / Constants
// -----------------------------------------------------------------------------

const ACCOUNT_ID = 7;
const bearerAuthHeader = `Bearer ${ACCOUNT_ID}`;

// Using dedicated IF Convention axios instance - baseURL already configured

// Default bearer config (used by most calls)
const config = {
  headers: { Authorization: bearerAuthHeader },
};

const buildTokenConfig = (authToken, extraHeaders = {}) => ({
  headers: {
    Authorization: `Token ${authToken.Token}_${ACCOUNT_ID}`,
    ...extraHeaders,
  },
});

const buildBearerConfig = (accountId = ACCOUNT_ID, extraHeaders = {}) => ({
  headers: {
    Authorization: `Bearer ${accountId}`,
    ...extraHeaders,
  },
});

const getErrorMessage = (error, defaultMessage) =>
  error?.response?.data?.message ||
  error?.response?.data ||
  error?.message ||
  defaultMessage;

// -----------------------------------------------------------------------------
// Pre-compiled rejection reasons
// -----------------------------------------------------------------------------

export const REJECTION_REASONS = {
  AT_CAPACITY: "I'm at capacity and can't accept more birds. You may request a different handler or select rotation for admin placement.",
  NOT_AVAILABLE: "Unable to accommodate at this time. Please try another handler.",
  REQUIREMENTS_NOT_MET: "Request does not meet handler requirements. Please contact admin for assistance.",
  CUSTOM: "" // For custom reasons
};

// Helper function to get all rejection reasons as an array for UI components
export function getRejectionReasons() {
  return [
    { key: 'AT_CAPACITY', label: 'At Capacity', text: REJECTION_REASONS.AT_CAPACITY },
    { key: 'NOT_AVAILABLE', label: 'Not Available', text: REJECTION_REASONS.NOT_AVAILABLE },
    { key: 'REQUIREMENTS_NOT_MET', label: 'Requirements Not Met', text: REJECTION_REASONS.REQUIREMENTS_NOT_MET }
  ];
}

// -----------------------------------------------------------------------------
// Convention: SignOn / PIN (new flow)
// -----------------------------------------------------------------------------

// Request PIN code (SignOn)
export async function requestPin(
  participantId,
  method,
  deviceId,
  accountId = `${ACCOUNT_ID}`
) {
  const endpoint = "Convention/SignOn";
  const requestData = { participantId, method, deviceId };

  try {
    const response = await ifConventionAxios.post(endpoint, requestData);

    return {
      success: response.data.success,
      verificationId: response.data.verificationId,
      expiresIn: response.data.expiresIn,
      message: response.data.message,
    };
  } catch (error) {
    return {
      success: false,
      message: getErrorMessage(error, "Failed to send PIN code"),
    };
  }
}

// Verify PIN code
export async function verifyPin(
  verificationId,
  pin,
  deviceId,
  accountId = `${ACCOUNT_ID}`
) {
  const endpoint = `Convention/VerifyPin`;
  const requestData = { verificationId, pin, deviceId };

  try {
    const response = await ifConventionAxios.post(endpoint, requestData, {
      headers: { Authorization: `Bearer ${accountId}` },
    });

    const participant = response.data.participantId
      ? {
          id: response.data.participantId,
          fullName: response.data.participantName,
          email: response.data.email,
          mobile: response.data.mobile,
        }
      : null;

    return {
      success: response.data.success,
      token: response.data.token,
      expiresIn: response.data.expiresIn,
      participant,
      message: response.data.message,
    };
  } catch (error) {
    return {
      success: false,
      message: getErrorMessage(error, "Invalid PIN code"),
    };
  }
}

// -----------------------------------------------------------------------------
// Convention: Handler requests
// -----------------------------------------------------------------------------

// Send handler request
export async function sendHandlerRequest(breederId, handlerId, requestData) {
  
  // Send breederId and handlerId as query params, rest as JSON body
  const endpoint = `Convention/SendHandlerRequest?breederId=${encodeURIComponent(breederId)}&handlerId=${encodeURIComponent(handlerId)}`;
  // Only send the requestData as body (do not include breederId/handlerId in body)
  try {
    const response = await ifConventionAxios.post(endpoint, requestData, config);
    // Notification temporarily disabled
    if (response.data && response.data.success) 
    {
      try {        
        await sendHandlerNotification(breederId, handlerId, requestData);
      } catch (notificationError) {
        // Don't fail the main request if notification fails
      }
    }
    return response.data;
  } catch (error) {
    return {
      success: false,
      message: getErrorMessage(error, "Failed to send handler request."),
    };
  }
}


// General handler requests loader (with isHandler flag)
export async function getHandlerRequests(participantId, isHandler = false) {
  const endpoint = `Convention/GetHandlerRequestsByParticipantId`;
  try {
    const response = await ifConventionAxios.get(endpoint, {
      params: { participantId, isHandler },
      ...config,
    });
    return response.data;
  } catch (error) {
    return {
      success: false,
      requests: [],
      message: getErrorMessage(error, "Failed to load handler requests."),
    };
  }
}

// For breeder: loads requests made by this breeder

export function getHandlerRequestsByBreederId(breederId) {
  return getHandlerRequests(breederId, false);
}

// For handler: loads requests assigned to this handler

export function getHandlerRequestsByHandlerId(handlerId) {
  return getHandlerRequests(handlerId, true);
}
// Get breeder requests (stub)
export async function getBreederRequests(breederId) {
  return { success: false, requests: [], message: "API not implemented" };
}

// Approve handler request
export async function approveHandlerRequest(requestId) {
  // Call Convention/UpdateHandlerRequestStatus with status=approved
  const endpoint = `Convention/UpdateHandlerRequestStatus/${requestId}`;
  try {
    const params = new URLSearchParams();
    params.append('status', 'approved');
    const response = await ifConventionAxios.post(`${endpoint}?${params.toString()}`, null, config);
    if (response.status === 200) {
      // Send notification to breeder about approval
      if (response.data && response.data.request) {
        try {
          await sendBreederNotification(
            response.data.request.breederId, 
            response.data.request.handlerId, 
            'approved', 
            response.data.request
          );
        } catch (notificationError) {
          // Don't fail the main request if notification fails
        }
      }
      return { success: true, request: response.data.request };
    }
    return {
      success: false,
      message: typeof response.statusText === 'string' ? response.statusText : JSON.stringify(response.statusText) || "Failed to approve handler request.",
    };
  } catch (error) {
    let msg = getErrorMessage(error, "Failed to approve handler request.");
    if (typeof msg !== 'string') {
      msg = JSON.stringify(msg);
    }
    return {
      success: false,
      message: msg,
    };
  }
}

// Reject handler request
export async function rejectHandlerRequest(requestId, handlerId, reason) {
  // Call Convention/UpdateHandlerRequestStatus with status=rejected and reason
  const endpoint = `Convention/UpdateHandlerRequestStatus/${requestId}`;
  
  // Use default rejection reason if none provided
  const rejectionReason = reason && reason.trim() ? reason : REJECTION_REASONS.AT_CAPACITY;
  
  try {
    const params = new URLSearchParams();
    params.append('status', 'rejected');
    params.append('reason', rejectionReason);
    

    
    const response = await ifConventionAxios.post(`${endpoint}?${params.toString()}`, null, config);
    if (response.status === 200) {
      // Send notification to breeder about rejection
      if (response.data && response.data.request) {
        try {
          await sendBreederNotification(
            response.data.request.breederId, 
            response.data.request.handlerId, 
            'rejected', 
            response.data.request,
            rejectionReason
          );
        } catch (notificationError) {
          // Don't fail the main request if notification fails
        }
      }
      return { success: true, request: response.data.request };
    }
    return {
      success: false,
      message: typeof response.statusText === 'string' ? response.statusText : JSON.stringify(response.statusText) || "Failed to reject handler request.",
    };
  } catch (error) {
    let msg = getErrorMessage(error, "Failed to reject handler request.");
    if (typeof msg !== 'string') {
      msg = JSON.stringify(msg);
    }
    return {
      success: false,
      message: msg,
    };
  }
}

// Get admin rotation requests
export async function getAdminRotationRequests() {
  try {
    const result = await getHandlerRequests('admin-rotation*', true);
    
    if (result && result.success && result.requests) {
      return {
        success: true,
        requests: result.requests,
        message: `Loaded ${result.requests.length} admin rotation requests`
      };
    } else {
      return {
        success: false,
        requests: [],
        message: result?.message || "No admin rotation requests found"
      };
    }
  } catch (error) {
    return {
      success: false,
      requests: [],
      message: getErrorMessage(error, "Failed to load admin rotation requests.")
    };
  }
}

// Assign handler to admin rotation
export async function assignHandlerToRotation(
  requestId,
  handlerId,
  birdsToAssign
) {
  // Try using the UpdateHandlerRequestStatus endpoint with assigned status and handler info
  const endpoint = `Convention/UpdateHandlerRequestStatus/${requestId}`;
  
  try {
    const params = new URLSearchParams();
    params.append('status', 'approved');
    params.append('assignedHandlerId', handlerId);
    params.append('assignedBirds', birdsToAssign.toString());
    
    const response = await ifConventionAxios.post(`${endpoint}?${params.toString()}`, null, config);
    
    if (response.status === 200) {
      return {
        success: true,
        remainingBirds: response.data?.remainingBirds || 0,
        message: response.data?.message || "Handler assigned successfully"
      };
    } else {
      return {
        success: false,
        message: response.data?.message || "Failed to assign handler"
      };
    }
  } catch (error) {
    
    // If the above approach doesn't work, try a different endpoint structure
    if (error.response?.status === 404 || error.message?.includes('Network Error')) {
      try {
        // Alternative: Try direct assignment endpoint
        const altEndpoint = `Convention/AssignRotationHandler`;
        const requestData = {
          rotationRequestId: requestId,
          handlerId: handlerId,
          birdsToAssign: birdsToAssign
        };
        
        const altResponse = await ifConventionAxios.post(altEndpoint, requestData, config);
        
        if (altResponse.data && altResponse.data.success) {
          return {
            success: true,
            remainingBirds: altResponse.data.remainingBirds || 0,
            message: altResponse.data.message || "Handler assigned successfully"
          };
        } else {
          return {
            success: false,
            message: altResponse.data?.message || "Failed to assign handler"
          };
        }
      } catch (altError) {
        return {
          success: false,
          message: getErrorMessage(altError, "Failed to assign handler to rotation. API endpoint may not be available.")
        };
      }
    }
    
    return {
      success: false,
      message: getErrorMessage(error, "Failed to assign handler to rotation.")
    };
  }
}

// Create new participant (Admin function)
export async function createParticipantAsAdmin(participantData) {
  const endpoint = `Convention/AddParticipant`;
  
  try {
    const response = await ifConventionAxios.post(endpoint, participantData, config);
    
    // If we get a 200/201 status, consider it successful even if success field is missing
    if (response.status === 200 || response.status === 201) {
      return {
        success: true,
        participant: response.data.participant || response.data,
        message: response.data?.message || "Participant created successfully"
      };
    } else {
      return {
        success: false,
        message: response.data?.message || "Failed to create participant"
      };
    }
    } catch (error) {
    return {
      success: false,
      message: getErrorMessage(error, "Failed to create participant.")
    };
  }
}

// Update participant information (Admin function)
export async function updateParticipantAsAdmin(participantId, participantData) {
  const endpoint = `Convention/UpdateParticipant/${participantId}`;
  
  try {
    
    const response = await ifConventionAxios.post(endpoint, participantData, config);
    
    
    // If we get a 200 status, consider it successful even if success field is missing
    if (response.status === 200 || response.status === 201) {
      return {
        success: true,
        participant: response.data.participant || response.data,
        message: response.data?.message || "Participant updated successfully"
      };
    } else {
      return {
        success: false,
        message: response.data?.message || "Failed to update participant"
      };
    }
    } catch (error) {
    return {
      success: false,
      message: getErrorMessage(error, "Failed to update participant.")
    };
  }
}

// Helper function to normalize participants response
function normalizeParticipants(participantsResponse) {
  if (Array.isArray(participantsResponse)) {
    return participantsResponse.map(p =>
      typeof p === 'string' ? JSON.parse(p) : p
    );
  } else if (typeof participantsResponse === 'string') {
    return JSON.parse(participantsResponse);
  }
  return [];
}

// Helper function to create notification message
function createNotificationMessage(breederName, requestData) {
  const birdText = requestData.numberOfBirds === 1 ? 'bird' : 'birds';
  const subject = `${breederName} selected you as a possible IF Convention handler`;
  
  // Build message with proper formatting
  let message = `${breederName} has sent you a request to handle ${requestData.numberOfBirds || 1} ${birdText} for the 2026 IF Convention Race.\n`;
  
  // Add breeder note if provided
  if (requestData.requestNote && requestData.requestNote.trim()) {
    message += `\nBreeder's note: "${requestData.requestNote}"\n`;
  }
  
  message += `\nPlease review and respond to this request in your handler dashboard:\nhttp://www.cjccombine.com/race/select-login\n\nIf you're not already signed in, you'll be taken to the sign-on page first.\n\nThank you,\n2026 IF Convention Race Team`;

  return { subject, message };
}

// Helper function to create notification request
function createNotificationRequest(subject, message, recipient, type) {
  return {
    AccountID: ACCOUNT_ID,
    Subject: subject,
    Message: message,
    Type: type,
    Recipient: recipient,
    SendToSubscribers: false
  };
}

// Send notification to handler about new request
async function sendHandlerNotification(breederId, handlerId, requestData) {
  // Skip notifications for admin rotation
  if (handlerId === 'admin-rotation' || handlerId === 'admin-rotation-mandatory') {
    return;
  }

  
  try {
    // Get and normalize participants data
    const participantsResponse = await GetParticipants();
    const participants = normalizeParticipants(participantsResponse);

    // Find breeder and handler
    const breeder = participants.find(p => p.id?.toString() === breederId?.toString());
    const handler = participants.find(p => p.id?.toString() === handlerId?.toString());

    if (!handler) {
      return;
    }

    // Get contact information
    const breederName = breeder?.fullName || 'A breeder';
    const handlerEmail = handler?.emailAddress || handler?.email;
    const handlerMobile = handler?.mobile;

    if (!handlerEmail && !handlerMobile) {
      return;
    }

    // Create notification content
    const { subject, message } = createNotificationMessage(breederName, requestData);

    // Check handler's preferred notification methods
    const preferredNotificationMethods = handler.preferredNotificationMethod || [];
    const allowEmail = preferredNotificationMethods.length === 0 || preferredNotificationMethods.includes('email');
    const allowSms = preferredNotificationMethods.includes('sms') || preferredNotificationMethods.includes('text');


    // Send notification based on handler preferences and available contact methods
    // Default to email if no preferences are specified
    const endpoint = `Convention/SendNotification`;
    
    if (handlerEmail && allowEmail) {
      const emailRequest = createNotificationRequest(subject, message, handlerEmail, 0); // Email = 0
      await ifConventionAxios.post(endpoint, emailRequest, config);
    } else if (handlerMobile && allowSms) {
      const smsRequest = createNotificationRequest(subject, message, handlerMobile, 1); // SMS = 1
      await ifConventionAxios.post(endpoint, smsRequest, config);
    } else {
      alert("No valid contact method available for handler notification.");
    }

  } catch (error) {
    throw error;
  }
}

// Helper function to create breeder notification message for request status updates
function createBreederNotificationMessage(handlerName, status, requestData, reason = null) {
  const birdText = requestData.numberOfBirds === 1 ? 'bird' : 'birds';
  const statusText = status === 'approved' ? 'APPROVED' : 'REJECTED';
  
  const subject = `Your Handler Request has been ${statusText}`;
  
  let message = `Your request for ${handlerName} to handle ${requestData.numberOfBirds || 1} ${birdText} for the 2026 IF Convention Race has been ${statusText.toLowerCase()}.`;

  if (status === 'approved') {
    message += `

${handlerName} has accepted your request and will be handling your ${birdText} for the convention race.`;
  } else if (status === 'rejected') {
    message += `

${handlerName} has declined your request.`;
    if (reason) {
      message += ` Reason: "${reason}"`;
    }
    message += `

You may want to contact another handler or reach out to the race committee for assistance.`;
  }

  message += `

You can view your handler requests by going to http://www.cjccombine.com/race/select-login.

If you're not already signed in, you'll be taken to the sign-on page first.

Thank you,
2026 IF Convention Race Team`;

  return { subject, message };
}

// Send notification to breeder about request status change (approved/rejected)
async function sendBreederNotification(breederId, handlerId, status, requestData, reason = null) {
  // Skip notifications for admin rotation
  if (handlerId === 'admin-rotation' || handlerId === 'admin-rotation-mandatory') {
    return;
  }

  try {
    // Get and normalize participants data
    const participantsResponse = await GetParticipants();
    const participants = normalizeParticipants(participantsResponse);

    // Find breeder and handler
    const breeder = participants.find(p => p.id?.toString() === breederId?.toString());
    const handler = participants.find(p => p.id?.toString() === handlerId?.toString());

    if (!breeder) {
      return;
    }

    // Get contact information
    const handlerName = handler?.fullName || 'The handler';
    const breederEmail = breeder?.emailAddress || breeder?.email;
    const breederMobile = breeder?.mobile;

    if (!breederEmail && !breederMobile) {
      return;
    }

    // Create notification content
    const { subject, message } = createBreederNotificationMessage(handlerName, status, requestData, reason);

    // For breeders, default to email notifications (they don't have handler-specific preferences)
    // We can add breeder notification preferences later if needed
    const endpoint = `Convention/SendNotification`;
    
    if (breederEmail) {
      const emailRequest = createNotificationRequest(subject, message, breederEmail, 0); // Email = 0
      await ifConventionAxios.post(endpoint, emailRequest, config);
    } else if (breederMobile) {
      const smsRequest = createNotificationRequest(subject, message, breederMobile, 1); // SMS = 1
      await ifConventionAxios.post(endpoint, smsRequest, config);
    }

  } catch (error) {
    throw error;
  }
}

// Cancel a handler request
export async function cancelHandlerRequest(participantId, requestId) {
  // Use the correct controller endpoint to update status to 'cancelled'
  const endpoint = `Convention/UpdateHandlerRequestStatus/${requestId}`;
  try {
    // status is required, reason is optional
    const params = new URLSearchParams();
    params.append('status', 'cancelled');
    // No reason for cancel from breeder
    const response = await ifConventionAxios.post(`${endpoint}?${params.toString()}`, null, config);
    if (response.status === 200) {
      return { success: true, request: response.data.request };
    }
    return {
      success: false,
      message: typeof response.statusText === 'string' ? response.statusText : JSON.stringify(response.statusText) || "Failed to cancel handler request.",
    };
  } catch (error) {
    let msg = getErrorMessage(error, "Failed to cancel handler request.");
    if (typeof msg !== 'string') {
      msg = JSON.stringify(msg);
    }
    return {
      success: false,
      message: msg,
    };
  }
}

// Update handler request (for bird details, notes, etc.)
export async function updateHandlerRequest(requestId, requestData) {
  const endpoint = `Convention/UpdateHandlerRequest/${requestId}`;
  try {
    const response = await ifConventionAxios.post(endpoint, requestData, config);
    if (response.status === 200 || response.status === 201) {
      return { 
        success: true, 
        request: response.data.request || response.data,
        message: response.data?.message || "Handler request updated successfully"
      };
    }
    return {
      success: false,
      message: response.data?.message || "Failed to update handler request",
    };
  } catch (error) {
    return {
      success: false,
      message: getErrorMessage(error, "Failed to update handler request."),
    };
  }
}

// -----------------------------------------------------------------------------
// Convention: Participants
// -----------------------------------------------------------------------------

// Get all participants
export async function GetParticipants() {
  const endpoint = `Convention/GetParticipants`;

  try {
    const response = await ifConventionAxios.get(endpoint, {
      headers: { Authorization: bearerAuthHeader },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
}

// Get a single participant by ID
export async function GetParticipantById(participantId) {
  const endpoint = `Participants/GetParticipant`;

  try {
    const response = await ifConventionAxios.get(endpoint, {
      headers: { Authorization: bearerAuthHeader },
      params: { participantId },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
}

// Save or update participant information
export async function SaveParticipant(participant, authToken) {
  const participantId = participant.id;
  if (!participantId) {
    throw new Error("Participant id is required");
  }

  const endpoint = `Convention/UpdateParticipant/${participantId}`;
  const requestConfig = buildTokenConfig(authToken, {
    "Content-Type": "application/json",
  });

  try {
    const response = await ifConventionAxios.post(endpoint, participant, requestConfig);
    return response.data;
  } catch (error) {
    throw error;
  }
}

// Delete a participant
export async function DeleteParticipant(participantId) {
  // Server route is "api/Convention/DeleteParticipantById/{participantId}" but ifConventionAxios baseURL already includes "api/"
  const endpoint = `Convention/DeleteParticipantById/${participantId}`;
  try {
    // Use HTTP DELETE method as specified in server
    const response = await ifConventionAxios.delete(endpoint, config);
    // Handle response exactly like updateParticipantAsAdmin
    if (response.status === 200 || response.status === 201 || response.status === 204) {
      return {
        success: true,
        message: response.data?.message || "Participant deleted successfully"
      };
    } else {
      return {
        success: false,
        message: response.data?.message || "Failed to delete participant"
      };
    }
  } catch (error) {
    return {
      success: false,
      message: getErrorMessage(error, "Failed to delete participant.")
    };
  }
}

// -----------------------------------------------------------------------------
// Auth: PIN / passcode / device auth
// -----------------------------------------------------------------------------

// Send PIN code to participant
export async function SendPincode(email, method, deviceId) {
  const endpoint = `Auth/SendPincode`;
  const data = { email, method, deviceId };

  try {
    const response = await ifConventionAxios.post(endpoint, data, config);
    return response.data;
  } catch (error) {
    throw error;
  }
}

// Verify PIN code
export async function VerifyPincode(email, pincode, deviceId) {
  const endpoint = `Auth/VerifyPincode`;
  const data = { email, pincode, deviceId };

  try {
    const response = await ifConventionAxios.post(endpoint, data, config);
    return response.data;
  } catch (error) {
    throw error;
  }
}

// Verify passcode (for email verification)
export async function VerifyPasscode(email, passcode, emailOnly = false) {
  const endpoint = `Auth/VerifyPasscode`;
  const data = { email, passcode, emailOnly };

  try {
    const response = await ifConventionAxios.post(endpoint, data, config);
    return response.data;
  } catch (error) {
    throw error;
  }
}

// Save device authentication
export async function SaveDeviceAuth(email, deviceId, passcode) {
  const endpoint = `Auth/SaveDeviceAuth`;
  const data = { email, deviceId, passcode };

  try {
    const response = await ifConventionAxios.post(endpoint, data, config);
    return response.data;
  } catch (error) {
    throw error;
  }
}

// Verify device authentication
export async function VerifyDeviceAuth(email, deviceId, passcode) {
  const endpoint = `Auth/VerifyDeviceAuth`;
  const data = { email, deviceId, passcode };

  try {
    const response = await ifConventionAxios.post(endpoint, data, config);
    return response.data;
  } catch (error) {
    throw error;
  }
}

// -----------------------------------------------------------------------------
// Requests (race requests)
// -----------------------------------------------------------------------------

// Get all requests for a race
export async function GetRequests(raceId, year) {
  const endpoint = `Requests/GetRequests`;

  try {
    const response = await ifConventionAxios.get(endpoint, {
      headers: { Authorization: bearerAuthHeader },
      params: { raceId, year },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
}

// Save a new request
export async function SaveRequest(request, authToken) {
  request.AccountID = ACCOUNT_ID;

  const endpoint = `Requests/SaveRequest`;
  const requestConfig = buildTokenConfig(authToken);

  try {
    const response = await ifConventionAxios.post(endpoint, request, requestConfig);
    return response.data;
  } catch (error) {
    throw error;
  }
}

// Update request status
export async function UpdateRequestStatus(requestId, status, authToken) {
  const endpoint = `Requests/UpdateStatus`;
  const requestConfig = buildTokenConfig(authToken);
  const data = { requestId, status };

  try {
    const response = await ifConventionAxios.post(endpoint, data, requestConfig);
    return response.data;
  } catch (error) {
    throw error;
  }
}
